export interface Team {
  id: number;
  name: string;
  shortName?: string;
  tla?: string;
  crest?: string;
}

export interface Score {
  home: number | null;
  away: number | null;
}

export interface Competition {
  id: number;
  name: string;
  code: string;
  emblem?: string;
}

export interface Match {
  id: number;
  competition: Competition;
  utcDate: string;
  status: 'SCHEDULED' | 'TIMED' | 'IN_PLAY' | 'PAUSED' | 'FINISHED' | 'POSTPONED' | 'CANCELLED' | 'SUSPENDED';
  matchday: number;
  stage: string;
  group: string | null;
  homeTeam: Team;
  awayTeam: Team;
  score: {
    fullTime: Score;
    halfTime: Score;
  };
}

export interface MatchesResponse {
  matches: Match[];
}

export interface MatchDay {
  date: string;
  matches: Match[];
}

export function groupByDate(matches: Match[]): MatchDay[] {
  const grouped = new Map<string, Match[]>();
  for (const m of matches) {
    const day = m.utcDate.slice(0, 10);
    if (!grouped.has(day)) grouped.set(day, []);
    grouped.get(day)!.push(m);
  }
  return Array.from(grouped.entries())
    .map(([date, matches]) => ({ date, matches }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function isMatchLive(m: Match) {
  return m.status === 'IN_PLAY' || m.status === 'PAUSED' || m.status === 'SUSPENDED';
}

export function isMatchUpcoming(m: Match) {
  return m.status === 'SCHEDULED' || m.status === 'TIMED';
}

export function formatMatchTime(utcDate: string): string {
  const d = new Date(utcDate);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function formatMatchDate(utcDate: string): string {
  const d = new Date(utcDate);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

export function timeUntil(utcDate: string): string {
  const diff = new Date(utcDate).getTime() - Date.now();
  if (diff <= 0) return '';
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours > 48) return `${Math.floor(hours / 24)}d`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

export interface Referee {
  id: number;
  name: string;
  type: string;
  nationality: string;
}

export interface Player {
  id: number;
  name: string;
  position: string | null;
  shirtNumber: number | null;
}

export interface Goal {
  minute: number;
  injuryTime: number | null;
  type: string;
  team: { id: number; name: string };
  scorer: { id: number; name: string };
  assist: { id: number; name: string } | null;
  score: { home: number; away: number };
}

export interface Booking {
  minute: number;
  team: { id: number; name: string };
  player: { id: number; name: string };
  card: 'YELLOW' | 'RED' | 'YELLOW_RED';
}

export interface Substitution {
  minute: number;
  team: { id: number; name: string };
  playerOut: { id: number; name: string };
  playerIn: { id: number; name: string };
}

export interface MatchDetail extends Match {
  venue: string;
  attendance: number | null;
  referees: Referee[];
  homeTeam: Team & { coach?: { id: number; name: string }; lineup?: Player[]; bench?: Player[] };
  awayTeam: Team & { coach?: { id: number; name: string }; lineup?: Player[]; bench?: Player[] };
  goals: Goal[];
  bookings: Booking[];
  substitutions: Substitution[];
  penalties?: { player: { id: number; name: string }; team: { id: number | null; name: string | null }; scored: boolean }[];
  odds?: { homeWin: number; draw: number; awayWin: number };
  head2head?: { numberOfMatches: number; totalGoals: number; homeTeam: { wins: number; draws: number; losses: number }; awayTeam: { wins: number; draws: number; losses: number } };
}

export const STAGE_ORDER: Record<string, number> = {
  'GROUP_STAGE': 1,
  'LAST_16': 2,
  'LAST_32': 3,
  'QUARTER_FINALS': 4,
  'SEMI_FINALS': 5,
  'THIRD_PLACE': 6,
  'FINAL': 7,
};

export function stageLabel(stage: string): string {
  const labels: Record<string, string> = {
    'GROUP_STAGE': 'Group Stage',
    'LAST_16': 'Round of 16',
    'LAST_32': 'Round of 32',
    'QUARTER_FINALS': 'Quarter-finals',
    'SEMI_FINALS': 'Semi-finals',
    'THIRD_PLACE': 'Third Place',
    'FINAL': 'Final',
    'PRELIMINARY_ROUND': 'Preliminary Round',
    'QUALIFICATION': 'Qualification',
    'QUALIFICATION_ROUND_1': 'Qualification Round 1',
    'QUALIFICATION_ROUND_2': 'Qualification Round 2',
    'QUALIFICATION_ROUND_3': 'Qualification Round 3',
    'PLAYOFF_ROUND_1': 'Play-off Round 1',
    'PLAYOFF_ROUND_2': 'Play-off Round 2',
    'PLAYOFFS': 'Play-offs',
    'REGULAR_SEASON': 'Regular Season',
  };
  return labels[stage] || stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getStage(match: Match): string {
  return match.stage || '';
}

export function formatMatchDateTime(utcDate: string, useUTC: boolean): string {
  const d = new Date(utcDate);
  if (useUTC) {
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    });
  }
  return formatMatchTime(utcDate);
}

export function formatMatchFullDate(utcDate: string, useUTC: boolean): string {
  const d = new Date(utcDate);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  if (useUTC) opts.timeZone = 'UTC';
  return d.toLocaleDateString('en-US', opts);
}
